/**
 * Native handoff (web → app) helpers.
 *
 * Apple / Google forbid selling subscriptions inside a mobile app, so
 * new AkountFly users are bounced to the browser for signup + trial +
 * card entry. The native app opens `akountfly.com/signup?src=app`. We
 * carry that `src=app` flag through the whole web flow so the setup-
 * complete page knows to render a "Open the AkountFly app" CTA that
 * deep-links back into the native app.
 *
 * The deep link carries a short-lived (180s) single-use OPAQUE authorization
 * code (`code` param) - never a session token. The PWA /auth/callback route
 * (loaded inside the mobile shell's WebView) exchanges it for the normal
 * cookie session so the user lands in the app already signed in.
 */

const STORAGE_KEY = 'akf_signup_src';
export const NATIVE_SIGNUP_SRC = 'app';

/** Plant the from-app flag directly (browser handoffs started inside the native shell). */
export const markFromNativeApp = () => {
  try { sessionStorage.setItem(STORAGE_KEY, NATIVE_SIGNUP_SRC); } catch { /* noop */ }
};

export const NATIVE_CALLBACK_SCHEME = 'akountfly:///auth/callback';
export const NATIVE_FALLBACK_LINK = 'akountfly://dashboard';

/**
 * Persist the signup source when a page URL carries `?src=app`. Call
 * this at the top of any page a user might land on with the flag
 * (currently /auth and /signup).
 */
export const persistSignupSrcFromUrl = () => {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const src = params.get('src');
    if (src) sessionStorage.setItem(STORAGE_KEY, src);
  } catch { /* noop */ }
};

/**
 * Was the signup initiated from the mobile app? True when the URL is
 * currently `?src=app` OR the sessionStorage flag was planted earlier
 * in the flow.
 */
export const isFromNativeApp = () => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('src') === NATIVE_SIGNUP_SRC) return true;
    return sessionStorage.getItem(STORAGE_KEY) === NATIVE_SIGNUP_SRC;
  } catch { return false; }
};

export const clearSignupSrc = () => {
  if (typeof window === 'undefined') return;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
};

/**
 * Request a one-time mobile authorization code for the signed-in web user
 * (they always are on the setup-complete page). Returns
 * `{code, expires_in, callback_url, universal_link}` or null on failure -
 * the CTA falls back to a plain akountfly://dashboard link and asks the
 * user to sign in.
 */
export const requestMobileAuthCode = async () => {
  try {
    const csrf = localStorage.getItem('akountfly_csrf');
    const res = await fetch('/api/auth/mobile/authorize', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

export const buildNativeDeepLink = (code) => {
  if (code) {
    return `${NATIVE_CALLBACK_SCHEME}?code=${encodeURIComponent(code)}`;
  }
  return NATIVE_FALLBACK_LINK;
};

/**
 * Exchange the one-time code (from /auth/callback?code=...) for the normal
 * cookie session. Resolves `{ok, status, data}`; never throws.
 */
export const exchangeMobileAuthCode = async (code) => {
  try {
    const res = await fetch('/api/auth/mobile/exchange', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    let data = null;
    try { data = await res.json(); } catch { /* non-JSON error body */ }
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
};
